import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Camera, MagnifyingGlass, Truck, Buildings, Warning, X, Info } from "@phosphor-icons/react";
import LivePlateScanner from "./LivePlateScanner";

const KIND_META = {
    owned_fleet:    { label: "Owned fleet",    tone: "bg-blue-100 text-blue-700 border-blue-200" },
    hired_fleet:    { label: "Hired fleet",    tone: "bg-purple-100 text-purple-700 border-purple-200" },
    transporter:    { label: "Contractor",     tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const LIC_META = {
    valid:          { label: "LICENCE OK",       tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    expiring_soon:  { label: "LICENCE SOON",     tone: "bg-amber-100 text-amber-700 border-amber-200" },
    expired:        { label: "LICENCE EXPIRED",  tone: "bg-rose-100 text-rose-700 border-rose-200" },
    unknown:        { label: "NO LICENCE DATA",  tone: "bg-slate-100 text-slate-500 border-slate-200" },
};

const fmtZAR = (n) => "R " + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "—";

/**
 * VehicleScanner — universal plate scanner across the entire Grindrod fleet.
 * Camera OCR + manual entry; returns owned/hired fleet & transporter matches
 * with full metadata + service history.
 */
export default function VehicleScanner() {
    const [plate, setPlate] = useState("");
    const [scanning, setScanning] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const search = async (p) => {
        const q = (p ?? plate).trim().toUpperCase();
        if (!q) return toast.error("Enter or scan a plate.");
        setBusy(true); setResult(null);
        try {
            const r = await api.get(`/fleet/scan-vehicle/${encodeURIComponent(q)}`);
            setResult(r.data);
            if (!r.data?.found) toast.info(`No vehicle found for ${q}.`);
        } catch (e) { toast.error(formatApiError(e)); }
        setBusy(false);
    };

    const onPlateCaptured = async (base64) => {
        setScanning(false); setBusy(true);
        try {
            const r = await api.post("/tronox/plate-scan", { image_base64: base64, mime_type: "image/jpeg" });
            if (r.data?.ok && r.data.plate) {
                setPlate(r.data.plate);
                await search(r.data.plate);
            } else {
                toast.error(r.data?.reason || "Could not read plate.");
                setBusy(false);
            }
        } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
    };

    if (scanning) return <LivePlateScanner onCapture={onPlateCaptured} onCancel={() => setScanning(false)} />;

    return (
        <div className="space-y-4" data-testid="vehicle-scanner-view">
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Fleet · Vehicle Scanner</div>
                <div className="text-xl font-black text-[var(--grindrod-navy)]">Scan any registered vehicle</div>
                <div className="text-xs text-slate-500 mt-0.5">Searches owned fleet, hired fleet and contractor trucks by plate.</div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); search(); }} className="bg-white border border-slate-200 rounded-lg p-3 flex gap-2" data-testid="vehicle-scanner-form">
                <input
                    value={plate}
                    onChange={(e) => setPlate(e.target.value.toUpperCase())}
                    placeholder="Type or scan a plate — e.g. ND 234 GP"
                    className="g-input flex-1 font-mono tracking-wider"
                    data-testid="vehicle-scanner-input"
                />
                <button type="button" onClick={() => setScanning(true)} disabled={busy} className="inline-flex items-center gap-1 px-3 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-300 text-white text-sm font-bold" data-testid="vehicle-scanner-camera">
                    <Camera size={16} weight="bold" /> Scan
                </button>
                <button type="submit" disabled={busy} className="inline-flex items-center gap-1 g-btn g-btn-primary" data-testid="vehicle-scanner-search">
                    <MagnifyingGlass size={16} weight="bold" /> {busy ? "Searching…" : "Search"}
                </button>
            </form>

            {result && (
                <div data-testid="vehicle-scanner-result">
                    <div className="flex items-baseline justify-between mb-2">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Result for</div>
                            <div className="font-mono text-2xl font-black text-[var(--grindrod-navy)]">{result.plate}</div>
                        </div>
                        <div className="text-xs text-slate-500">{result.matches.length} match{result.matches.length !== 1 ? "es" : ""}</div>
                    </div>

                    {!result.found ? (
                        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="vehicle-scanner-no-match">
                            <Warning size={28} className="mx-auto text-amber-500 mb-2" weight="fill" />
                            <div className="text-slate-700 font-bold">No vehicle registered with this plate.</div>
                            <div className="text-slate-500 text-sm mt-1">Try a different plate or register the vehicle under Fleet or Transporters.</div>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {result.matches.map((m, i) => <MatchCard key={i} match={m} />)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function MatchCard({ match }) {
    const km = KIND_META[match.kind] || { label: match.kind_label || match.kind, tone: "bg-slate-100 text-slate-700 border-slate-200" };
    const lm = LIC_META[match.license?.status || "unknown"];
    return (
        <div className="rounded-lg border-2 border-slate-200 bg-white overflow-hidden shadow-sm" data-testid={`vs-match-${match.kind}`}>
            <div className={`px-4 py-2 border-b border-slate-200 flex items-center gap-2 ${km.tone}`}>
                {match.kind === "transporter" ? <Buildings size={16} weight="fill" /> : <Truck size={16} weight="fill" />}
                <span className="uppercase text-[11px] tracking-widest font-black">{km.label}</span>
                <span className="ml-auto flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border" style={{}} title={match.license?.days_until_expiry != null ? `${Math.abs(match.license.days_until_expiry)} days` : "no data"}>
                    <span className={`px-2 py-0.5 rounded border ${lm.tone}`}>
                        {lm.label}{match.license?.status !== "unknown" && ` · ${match.license.days_until_expiry >= 0 ? `${match.license.days_until_expiry}d left` : `${Math.abs(match.license.days_until_expiry)}d ago`}`}
                    </span>
                </span>
            </div>
            {match.kind === "transporter" ? <TransporterPanel match={match} /> : <FleetPanel match={match} />}
        </div>
    );
}

function KV({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className="text-sm text-slate-900 font-semibold">{value || "—"}</div>
        </div>
    );
}

function FleetPanel({ match }) {
    const m = match.machine || {};
    return (
        <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KV label="Machine number" value={m.machine_number} />
                <KV label="Make / Model" value={[m.make, m.model].filter(Boolean).join(" / ")} />
                <KV label="Colour" value={m.color} />
                <KV label="Reg plate" value={m.registration_plate} />
                <KV label="Category" value={m.category} />
                <KV label="Owner division" value={m.owner_division} />
                <KV label="Current hours" value={m.current_hours != null ? `${m.current_hours} h` : "—"} />
                <KV label="Status" value={m.service_status?.replace(/_/g, " ")} />
                {m.is_hired && <KV label="Hire company" value={m.hire_company} />}
                {m.is_hired && <KV label="Hire rate / h" value={m.hire_rate_per_hour ? fmtZAR(m.hire_rate_per_hour) : "—"} />}
                <KV label="Licence disk #" value={m.license_disk_number} />
                <KV label="Licence expiry" value={m.license_expiry_date} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatTile label="Fuel entries" value={(match.fuel_entries || []).length} />
                <StatTile label="Fuel spend" value={fmtZAR(match.fuel_total_zar)} />
                <StatTile label="Breakdowns" value={(match.breakdown_entries || []).length} sub={fmtZAR(match.breakdown_total_zar)} />
                <StatTile label="Operating sessions" value={match.sessions_total} />
            </div>

            {(match.fuel_entries || []).length > 0 && (
                <HistoryTable
                    title="Fuel history"
                    rows={match.fuel_entries.slice(0, 10)}
                    cols={[
                        { key: "entry_date",  label: "Date",   fmt: fmtDate },
                        { key: "litres",      label: "Litres", cls: "text-right tabular" },
                        { key: "cost_zar",    label: "Cost",   fmt: fmtZAR, cls: "text-right tabular" },
                        { key: "hours",       label: "Hours",  cls: "text-right tabular" },
                        { key: "notes",       label: "Notes",  cls: "text-slate-500" },
                    ]}
                />
            )}
            {(match.breakdown_entries || []).length > 0 && (
                <HistoryTable
                    title="Breakdown history"
                    rows={match.breakdown_entries.slice(0, 10)}
                    cols={[
                        { key: "entry_date",   label: "Date",      fmt: fmtDate },
                        { key: "reason",       label: "Reason" },
                        { key: "cost_zar",     label: "Cost",      fmt: fmtZAR, cls: "text-right tabular" },
                        { key: "downtime_hrs", label: "Downtime",  cls: "text-right tabular" },
                        { key: "notes",        label: "Notes",     cls: "text-slate-500" },
                    ]}
                />
            )}
            {(match.fel_sessions || []).length > 0 && (
                <HistoryTable
                    title="Recent operating sessions"
                    rows={match.fel_sessions.slice(0, 10)}
                    cols={[
                        { key: "started_at",     label: "Started",     fmt: fmtDate },
                        { key: "stopped_at",     label: "Stopped",     fmt: fmtDate },
                        { key: "operation_type", label: "Type" },
                        { key: "operator_name",  label: "Operator" },
                        { key: "delays",         label: "Delays", fmt: (v) => Array.isArray(v) ? v.length : 0, cls: "text-right tabular" },
                    ]}
                />
            )}
        </div>
    );
}

function TransporterPanel({ match }) {
    const tr = match.truck || {};
    const tp = match.transporter || {};
    return (
        <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KV label="Plate" value={tr.plate} />
                <KV label="Make / Model" value={[tr.make, tr.model].filter(Boolean).join(" / ")} />
                <KV label="Colour" value={tr.color} />
                <KV label="Licence expiry" value={tr.license_expiry_date} />
                <KV label="Transporter" value={tp.company_name} />
                <KV label="VAT #" value={tp.vat_number} />
                <KV label="Contact" value={tp.emergency_contact_name} />
                <KV label="Phone" value={tp.emergency_contact_phone} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <StatTile label="Trips logged" value={match.trips_total} />
                <StatTile label="Safety incidents" value={(match.incidents || []).length} />
                <StatTile label="Registered docs" value={(tp.documents || []).length} />
            </div>

            {(match.trips || []).length > 0 && (
                <HistoryTable
                    title="Recent trips (Tronox exports)"
                    rows={match.trips.slice(0, 10)}
                    cols={[
                        { key: "arrived_at", label: "Arrived",   fmt: fmtDate },
                        { key: "weighed_at", label: "Weighed",   fmt: fmtDate },
                        { key: "silo",       label: "Silo" },
                        { key: "tons",       label: "Tons",      cls: "text-right tabular" },
                        { key: "status",     label: "Status",    cls: "text-slate-500" },
                    ]}
                />
            )}
            {(match.incidents || []).length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-rose-600 font-bold mb-1">Safety incidents</div>
                    <div className="space-y-1">
                        {match.incidents.slice(0, 10).map((i) => (
                            <div key={i.id} className="rounded border border-rose-200 bg-rose-50 p-2 text-xs" data-testid={`vs-incident-${i.id}`}>
                                <div className="flex items-center justify-between text-[10px] text-rose-700 font-bold uppercase">
                                    <span>{fmtDate(i.occurred_at)}</span>
                                    <span>{(i.incident_type || "").replace(/_/g, " ")}</span>
                                </div>
                                <div className="text-slate-800 mt-0.5">{i.description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, sub }) {
    return (
        <div className="rounded bg-slate-50 border border-slate-200 py-2 px-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className="text-lg font-black text-[var(--grindrod-navy)]">{value ?? 0}</div>
            {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
        </div>
    );
}

function HistoryTable({ title, rows, cols }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{title}</div>
            <div className="rounded border border-slate-200 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                        <tr>{cols.map((c) => <th key={c.key} className={`px-2 py-1.5 text-left ${c.cls || ""}`}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-t border-slate-100">
                                {cols.map((c) => {
                                    const raw = r[c.key];
                                    const v = c.fmt ? c.fmt(raw) : (raw ?? "—");
                                    return <td key={c.key} className={`px-2 py-1.5 ${c.cls || ""}`}>{v == null || v === "" ? "—" : v}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
